import type { AppConfig, AppServices } from '../../interfaces/src';
import type { SupabaseClientOptions } from '@supabase/supabase-js';
import { SupabaseDataService } from './data-service';
import { SupabaseAuthService } from './auth-service';
import { SupabaseStorageService } from './storage-service';
import { StubPaymentService } from './stub-payment-service';
import { getSupabaseClient } from './client';

export function createSupabaseServices(config: AppConfig): AppServices {
  // Storage follows the data layer: uploads land on the tenant when one is
  // provisioned, otherwise on the master (the "everything in master by default"
  // mode). Auth always lives on master.
  const storageUrl = config.tenantUrl ?? config.masterUrl;
  const storageKey = config.tenantAnonKey ?? config.masterAnonKey;
  // Force the master client to be created with the custom session storage
  // BEFORE the auth service touches it — getSupabaseClient memoises by URL,
  // so the first call wins. This is the seam that makes desktop sessions
  // land in the OS keychain instead of localStorage.
  //
  // Skip when URL/key are empty (no .env yet) — the underlying services
  // already defer the createClient call until a real call is made, so we
  // shouldn't throw "supabaseUrl is required" at boot just to prime the
  // cache.
  const authOptions = config.disableAuthPersistence
    ? {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
          detectSessionInUrl: false,
        },
      }
    : config.sessionStorage
      ? {
          auth: {
            // Supabase forwards these directly to GoTrueClient. Async
            // getItem is honoured by gotrue-js as long as we set
            // persistSession: true (the default).
            storage: config.sessionStorage as Storage,
            persistSession: true,
            autoRefreshToken: true,
            detectSessionInUrl: true,
          },
        }
      : undefined;
  const clientOptions = withPlatformAdminContext(
    authOptions,
    config.adminTenantId,
    config.adminAccessToken,
  );

  if (clientOptions && config.masterUrl && config.masterAnonKey) {
    getSupabaseClient(config.masterUrl, config.masterAnonKey, clientOptions);
  }
  return {
    dataService: new SupabaseDataService(
      config.masterUrl,
      config.masterAnonKey,
      config.tenantUrl,
      config.tenantAnonKey,
      clientOptions,
    ),
    authService: new SupabaseAuthService(
      config.masterUrl,
      config.masterAnonKey,
      clientOptions,
      config.adminAccessToken,
    ),
    storageService: new SupabaseStorageService(storageUrl, storageKey, clientOptions),
    paymentService: new StubPaymentService(),
  };
}

function withPlatformAdminContext(
  options: SupabaseClientOptions<'public'> | undefined,
  adminTenantId: string | undefined,
  adminAccessToken: string | undefined,
): SupabaseClientOptions<'public'> | undefined {
  if (!adminTenantId && !adminAccessToken) return options;

  return {
    ...(options ?? {}),
    global: {
      ...(options?.global ?? {}),
      headers: {
        ...(options?.global?.headers ?? {}),
        ...(adminAccessToken ? { Authorization: `Bearer ${adminAccessToken}` } : {}),
        ...(adminTenantId ? { 'x-lyfty-admin-tenant-view': adminTenantId } : {}),
      },
    },
  };
}
